// ============================================================
// SecaoAvaliacaoTecnica.js — a tela da avaliação técnica
//
// Mesmo desenho da avaliação física, e de propósito: a lista de atletas,
// a ficha de um atleta com a série, e o formulário de lançamento na
// mesma tela. Quem avalia abre o atleta, dá as seis notas olhando o
// treino e vê o resultado aparecer ali.
//
// Nenhuma conta mora aqui. Somatório, média, faixa da escala, conversão
// de tempo em nota e evolução saem de `avaliacaoTecnica.js`, e o escore
// global de `desempenho.js`, que são os mesmos módulos que o PDF usa.
//
// ── O que esta tela carrega, e por quê ──────────────────────
//
// Ela lê as DUAS coleções, a técnica e a dos testes físicos, mesmo sendo
// a tela da técnica. O motivo é o escore global: ele é 75% técnica e 25%
// capacidade física, e mostrar a metade técnica sem dizer que a outra
// metade existe faria o técnico achar que terminou a avaliação do atleta
// quando falta o preparador lançar a dele.
// ============================================================

import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  carregarMembrosGestao, carregarAvaliacoesTecnicas, carregarTestesFisicos,
  salvarAvaliacaoTecnica, carregarCriteriosTecnicos, salvarCriteriosTecnicos,
} from "../firebase";
import {
  modeloDe, compilarSerieTecnica, criteriosDaModalidade, gruposDaModalidade,
  agruparPorGrupo as porGrupo,
  validarCriterioNovo, laudoTecnico, faixaDaNota, tempoDePraticaEmTexto,
  NOTA_MAXIMA, NOTA_MINIMA, rotuloDaAusencia, escalaDe,
} from "../avaliacaoTecnica";
import { compilarSerieDeTestes } from "../testesFisicos";
import { escoreGlobal, serieDeEscores } from "../desempenho";
import { idadeEm, idadeDecimalEm } from "../idade";
import { gerarPDFAvaliacaoTecnica, gerarPDFFichaDeAvaliacaoTecnica } from "../pdfRelatorio";
import { useNomeNucleo } from "../NucleosContexto";
import {
  TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO,
  AZUL, OK, ATENCAO, RUIM,
} from "../tema";
import {
  Card, Btn, Sel, Input, StatusBox, Carregando, Vazio,
  Estatistica, FileiraDeEstatisticas, CampoDeBusca, GraficoDeMedida,
  EscoreGlobal, LegendaDosNiveis, LegendaDaEscala,
} from "../componentes";
import useIsMobile from "../useIsMobile";

const HOJE = () => new Date().toISOString().slice(0, 10);

// As marcas de ausência de um critério, com os mesmos códigos que a
// coleta de testes usa e que `avaliacaoTecnica.js` sabe ler.
const MARCAS_DE_AUSENCIA = [
  { value: "", label: "avaliado" },
  { value: "naoRealizado", label: "Não realizado" },
  { value: "falta", label: "Falta" },
  { value: "dispensaMedica", label: "Dispensa médica" },
];

export default function SecaoAvaliacaoTecnica({ userNucleo, podeEditar = true }) {
  const isMobile = useIsMobile();
  const nucleo = userNucleo || "";

  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [atletas, setAtletas] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [testes, setTestes] = useState([]);
  const [busca, setBusca] = useState("");
  const [modalidade, setModalidade] = useState("");
  const [selecionado, setSelecionado] = useState(null);
  // A ficha de critérios de cada modalidade do núcleo, como a comissão a
  // deixou. Chave: o nome da modalidade como o painel o escreve.
  const [configCriterios, setConfigCriterios] = useState({});

  const recarregar = useCallback(async () => {
    if (!nucleo) return;
    setCarregando(true);
    try {
      // `carregarMembrosGestao` porque esta tela precisa de modalidade e
      // de data de nascimento, e o carregador da grade de presença não
      // devolve nenhuma das duas.
      const [pessoas, tecnicas, fisicos, criterios] = await Promise.all([
        carregarMembrosGestao(nucleo),
        carregarAvaliacoesTecnicas(nucleo),
        carregarTestesFisicos(nucleo),
        // A ficha de critérios é a única das quatro leituras que pode
        // faltar sem a tela deixar de funcionar: sem ela, a modalidade
        // usa o modelo do código, que é o que o portal fez até aqui. Por
        // isso ela cai para vazio em vez de derrubar a tela, e é o que
        // segura o dia em que as regras do Firestore chegarem depois da
        // publicação do painel.
        carregarCriteriosTecnicos(nucleo).catch(() => ({})),
      ]);
      setAtletas(pessoas.filter(p => (p.perfil || "").toLowerCase().includes("atleta")));
      setAvaliacoes(tecnicas);
      setTestes(fisicos);
      setConfigCriterios(criterios);
    } catch (e) {
      setStatus({ tipo: "erro", texto: `Não consegui carregar: ${e.message}` });
    } finally {
      setCarregando(false);
    }
  }, [nucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const modalidades = useMemo(
    () => [...new Set(atletas.map(a => a.modalidade).filter(Boolean))].sort(),
    [atletas],
  );

  // O inativo sai da lista e as avaliações dele ficam, como na avaliação
  // física: "quem eu avalio este mês" e "o que foi avaliado" são duas
  // perguntas, e apagar o histórico de quem saiu mudaria a média de
  // janeiro a cada desligamento.
  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return atletas
      .filter(a => a.ativo !== false)
      .filter(a => !modalidade || a.modalidade === modalidade)
      .filter(a => !termo || (a.nome || "").toLowerCase().includes(termo))
      .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
  }, [atletas, busca, modalidade]);

  const porAtleta = useMemo(() => indexar(avaliacoes), [avaliacoes]);
  const testesPorAtleta = useMemo(() => indexar(testes), [testes]);

  if (!nucleo) return <Vazio titulo="Selecione um núcleo." />;
  if (carregando) return <Carregando />;

  const atleta = selecionado ? atletas.find(a => a.uid === selecionado) : null;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      {status && <StatusBox tipo={status.tipo} msg={status.texto} />}

      {atleta ? (
        <FichaDoAtleta
          atleta={atleta}
          avaliacoes={porAtleta[atleta.uid] || []}
          testes={testesPorAtleta[atleta.uid] || []}
          config={configCriterios[atleta.modalidade] || null}
          nucleo={nucleo}
          podeEditar={podeEditar}
          isMobile={isMobile}
          onVoltar={() => setSelecionado(null)}
          onSalvou={async () => {
            await recarregar();
            setStatus({ tipo: "ok", texto: "Avaliação técnica gravada." });
          }}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      ) : (
        <ListaDeAtletas
          lista={lista}
          porAtleta={porAtleta}
          testesPorAtleta={testesPorAtleta}
          modalidades={modalidades}
          modalidade={modalidade}
          setModalidade={setModalidade}
          busca={busca}
          setBusca={setBusca}
          nucleo={nucleo}
          config={configCriterios}
          podeEditar={podeEditar}
          isMobile={isMobile}
          onAbrir={setSelecionado}
          onSalvouCriterios={async texto => {
            await recarregar();
            setStatus({ tipo: "ok", texto });
          }}
          onErro={msg => setStatus({ tipo: "erro", texto: msg })}
        />
      )}
    </div>
  );
}

function indexar(lista) {
  const mapa = {};
  lista.forEach(x => {
    if (!x.atletaUid) return;
    (mapa[x.atletaUid] = mapa[x.atletaUid] || []).push(x);
  });
  return mapa;
}

// ── Lista ────────────────────────────────────────────────────

function ListaDeAtletas({
  lista, porAtleta, testesPorAtleta, modalidades, modalidade, setModalidade,
  busca, setBusca, nucleo, config, podeEditar, isMobile, onAbrir,
  onSalvouCriterios, onErro,
}) {
  const nomeDoNucleo = useNomeNucleo();
  const [gerando, setGerando] = useState(false);
  const comAvaliacao = lista.filter(a => (porAtleta[a.uid] || []).length > 0).length;
  // Quem tem teste físico e não tem avaliação técnica. É o buraco que
  // esta tela existe para fechar, e aparece em número na primeira linha.
  const soComTeste = lista.filter(
    a => (testesPorAtleta[a.uid] || []).length > 0 && !(porAtleta[a.uid] || []).length,
  ).length;

  async function baixarFichas() {
    setGerando(true);
    try {
      await gerarPDFFichaDeAvaliacaoTecnica({
        atletas: lista, nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar as fichas: ${e.message}`);
    } finally {
      setGerando(false);
    }
  }

  return (
    <Fragment>
    <Card>
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Atletas" valor={lista.length} />
        <Estatistica rotulo="Com avaliação técnica" valor={comAvaliacao} />
        <Estatistica rotulo="Sem avaliação" valor={lista.length - comAvaliacao} />
        <Estatistica rotulo="Com teste físico e sem a técnica" valor={soComTeste} />
      </FileiraDeEstatisticas>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: `${ESP.lg}px 0` }}>
        <CampoDeBusca valor={busca} onChange={setBusca} placeholder="Buscar atleta" />
        <Sel value={modalidade} onChange={setModalidade}
          options={[{ value: "", label: "Todas as modalidades" },
            ...modalidades.map(m => ({ value: m, label: m }))]} />
        {lista.length > 0 && (
          <Btn variant="secondary" onClick={baixarFichas} disabled={gerando}>
            {gerando ? "Gerando…" : `Fichas de avaliação em papel (${lista.length})`}
          </Btn>
        )}
      </div>

      {lista.length === 0 ? (
        <Vazio titulo="Nenhum atleta neste recorte." />
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {lista.map(a => {
            const avs = porAtleta[a.uid] || [];
            const ultima = avs[avs.length - 1];
            const serie = compilarSerieTecnica({ modalidade: a.modalidade, avaliacoes: avs });
            const ponto = serie[serie.length - 1] || null;
            const faixa = ponto ? faixaDaNota(ponto.media) : null;
            return (
              <button key={a.uid} onClick={() => onAbrir(a.uid)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, width: "100%", textAlign: "left", cursor: "pointer",
                background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
                padding: "11px 13px", font: "inherit",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: TEXTO, ...TIPO.corpo }}>{a.nome}</div>
                  <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                    {a.modalidade || "sem modalidade"}
                    {a.dataNascimento && ` · ${idadeEm(a.dataNascimento)} anos`}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {ponto && Number.isFinite(ponto.media) ? (
                    <div style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>
                      {pt(ponto.media, 2)}
                      <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                        {faixa ? ` · ${faixa.rotulo.toLowerCase()}` : ""}
                      </span>
                    </div>
                  ) : (
                    <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>sem avaliação</div>
                  )}
                  {ultima && (
                    <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                      última em {emBR(ultima.data)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>

    {/* A ficha de critérios de cada modalidade. Fica na lista, e não
        dentro da ficha de um atleta, porque é decisão de modalidade e não
        de atleta: mudar os critérios muda a próxima coleta de todo mundo
        que treina aquilo. */}
    {(modalidade ? [modalidade] : modalidades).map(m => (
      <FichaDeCriterios
        key={m}
        modalidade={m}
        config={config[m] || null}
        nucleo={nucleo}
        podeEditar={podeEditar}
        isMobile={isMobile}
        onSalvou={onSalvouCriterios}
        onErro={onErro}
      />
    ))}
    </Fragment>
  );
}

// ── A ficha de critérios de uma modalidade ───────────────
//
// O que a comissão avalia numa modalidade não é para sempre. A ficha do
// tênis de mesa cresceu de seis critérios para dezenove em outubro de
// 2026, e a do BMX perdeu os dois tempos de pista no mesmo mês. Esta tela
// é onde isso passa a acontecer sem mexer no código: a coordenação e o
// técnico da modalidade acrescentam o que vão avaliar e desativam o que
// pararam de avaliar.
//
// Duas coisas que ela NÃO faz, e é por isso que ela pode existir:
//
//   1. Não mexe em avaliação gravada. Cada avaliação carrega a lista de
//      critérios dela dentro do documento, com o divisor do dia. A média
//      de janeiro continua dividindo pelos critérios de janeiro, e é o
//      que deixa a série comparável.
//
//   2. Não apaga o modelo. O que fica gravado é a diferença: os
//      acrescentados e os desativados. Assim uma revisão do modelo no
//      código chega a todos os núcleos, inclusive aos que já mexeram.
function FichaDeCriterios({
  modalidade, config, nucleo, podeEditar, isMobile, onSalvou, onErro,
}) {
  const [aberta, setAberta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novo, setNovo] = useState({ rotulo: "", grupo: "", grupoNovo: "", detalhe: "" });

  const modelo = modeloDe(modalidade);
  const extras = (config && config.extras) || [];
  const desativados = (config && config.desativados) || [];
  const emUso = criteriosDaModalidade(modalidade, config);
  const grupos = gruposDaModalidade(modalidade, config);

  async function gravar(mudanca, texto) {
    setSalvando(true);
    try {
      await salvarCriteriosTecnicos({ nucleo, modalidade, extras, desativados, ...mudanca });
      await onSalvou(texto);
    } catch (e) {
      onErro(`Não consegui gravar a ficha de ${modalidade}: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  }

  function acrescentar() {
    const grupo = novo.grupo === "__novo" ? novo.grupoNovo : novo.grupo;
    const { erro, criterio } = validarCriterioNovo({ ...novo, grupo }, emUso);
    if (erro) return onErro(erro);
    setNovo({ rotulo: "", grupo: novo.grupo, grupoNovo: novo.grupoNovo, detalhe: "" });
    return gravar(
      { extras: [...extras, criterio] },
      `${criterio.rotulo} entra na ficha de ${modalidade} a partir da próxima avaliação.`,
    );
  }

  function alternar(c) {
    const doModelo = modelo.criterios.some(x => x.id === c.id);
    if (!doModelo) {
      // Critério acrescentado aqui sai da lista de vez: ele nunca foi
      // modelo, e guardar um "desativado" que não existe no código seria
      // guardar lixo. As avaliações que o usaram continuam com ele.
      return gravar(
        { extras: extras.filter(x => x.id !== c.id) },
        `${c.rotulo} sai da ficha de ${modalidade}. As avaliações já lançadas permanecem `
        + "com o critério.",
      );
    }
    const jaFora = desativados.includes(c.id);
    return gravar(
      {
        desativados: jaFora
          ? desativados.filter(id => id !== c.id)
          : [...desativados, c.id],
      },
      jaFora
        ? `${c.rotulo} volta à ficha de ${modalidade}.`
        : `${c.rotulo} sai das próximas avaliações de ${modalidade}.`,
    );
  }

  const fora = modelo.criterios.filter(c => desativados.includes(c.id));

  return (
    <Card titulo={`Critérios de ${modalidade}`}>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
        {`${emUso.length} ${emUso.length === 1 ? "critério" : "critérios"} na próxima avaliação`}
        {extras.length > 0 && `, ${extras.length} acrescentado${extras.length > 1 ? "s" : ""} aqui`}
        {fora.length > 0 && `, ${fora.length} desativado${fora.length > 1 ? "s" : ""}`}
        {". Alterações não afetam avaliações já lançadas: cada uma registra os critérios "}
        {"vigentes na data."}
      </div>
      <div style={{ marginTop: ESP.md }}>
        <Btn variant="secondary" onClick={() => setAberta(a => !a)}>
          {aberta ? "Fechar a ficha" : "Ver e ajustar os critérios"}
        </Btn>
      </div>

      {aberta && (
        <div style={{ marginTop: ESP.lg, display: "grid", gap: ESP.md }}>
          {emUso.length === 0 && (
            <StatusBox tipo="aviso" msg={
              `${modalidade} ainda não tem critérios. Acrescente abaixo os que a comissão `
              + "avalia, e eles aparecerão na próxima avaliação."
            } />
          )}

          <div style={{ display: "grid", gap: 6 }}>
            {emUso.map(c => {
              const doModelo = modelo.criterios.some(x => x.id === c.id);
              return (
                <div key={c.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 10, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
                  padding: "8px 11px", flexWrap: isMobile ? "wrap" : "nowrap",
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 600 }}>
                      {c.rotulo}
                      {!doModelo && (
                        <span style={{ ...TIPO.apoio, fontWeight: 400, color: AZUL }}>
                          {" · acrescentado neste núcleo"}
                        </span>
                      )}
                    </div>
                    {(c.grupo || c.detalhe) && (
                      <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                        {[c.grupo, c.detalhe].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  {podeEditar && (
                    <Btn variant="secondary" onClick={() => alternar(c)} disabled={salvando}>
                      {doModelo ? "Desativar" : "Remover"}
                    </Btn>
                  )}
                </div>
              );
            })}
          </div>

          {fora.length > 0 && (
            <div>
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginBottom: 6 }}>
                Fora das próximas avaliações. As já lançadas permanecem com esses critérios.
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                {fora.map(c => (
                  <div key={c.id} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 10, border: `1px dashed ${BORDA}`, borderRadius: RAIO.md,
                    padding: "8px 11px", background: FUNDO_SUAVE,
                  }}>
                    <div style={{ ...TIPO.corpo, color: CINZA_ESC }}>
                      {c.rotulo}
                      <span style={{ ...TIPO.apoio }}>{c.grupo ? ` · ${c.grupo}` : ""}</span>
                    </div>
                    {podeEditar && (
                      <Btn variant="secondary" onClick={() => alternar(c)} disabled={salvando}>
                        Reativar
                      </Btn>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {podeEditar && (
            <div style={{
              border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "11px 12px",
              background: FUNDO_SUAVE,
            }}>
              <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 600 }}>
                Acrescentar um critério
              </div>
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
                O critério passa a valer a partir da próxima avaliação. O grupo organiza as
                partes da ficha na tela e no papel, como os golpes do tênis de mesa.
              </div>
              <div style={{
                display: "grid", gap: 10, marginTop: ESP.md,
                gridTemplateColumns: isMobile ? "1fr" : "minmax(190px, 1.2fr) 180px 1fr auto",
                alignItems: "end",
              }}>
                <Input label="Critério" value={novo.rotulo}
                  onChange={v => setNovo(n => ({ ...n, rotulo: v }))}
                  placeholder="o que o técnico vai observar" />
                <div>
                  <Sel label="Grupo" value={novo.grupo}
                    onChange={v => setNovo(n => ({ ...n, grupo: v }))}
                    options={[
                      { value: "", label: "sem grupo" },
                      ...grupos.map(g => ({ value: g, label: g })),
                      { value: "__novo", label: "outro grupo…" },
                    ]} />
                  {novo.grupo === "__novo" && (
                    <Input label="Nome do grupo" value={novo.grupoNovo}
                      onChange={v => setNovo(n => ({ ...n, grupoNovo: v }))} />
                  )}
                </div>
                <Input label="Detalhe (opcional)" value={novo.detalhe}
                  onChange={v => setNovo(n => ({ ...n, detalhe: v }))}
                  placeholder="o que conta como nota alta" />
                <Btn onClick={acrescentar} disabled={salvando || !novo.rotulo.trim()}>
                  {salvando ? "Gravando…" : "Acrescentar"}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Ficha do atleta ──────────────────────────────────────────

function FichaDoAtleta({
  atleta, avaliacoes, testes, config, nucleo, podeEditar, isMobile,
  onVoltar, onSalvou, onErro,
}) {
  const nomeDoNucleo = useNomeNucleo();
  const [editando, setEditando] = useState(null);
  const [imprimindo, setImprimindo] = useState(false);
  const [gerandoFicha, setGerandoFicha] = useState(false);
  const [abertaEm, setAbertaEm] = useState(null);

  const serie = useMemo(
    () => compilarSerieTecnica({ modalidade: atleta.modalidade, avaliacoes }),
    [atleta.modalidade, avaliacoes],
  );

  // A série de testes físicos entra só para o escore global. Ela é lida
  // aqui, e não recompilada de outro jeito, para o número desta tela ser
  // igual ao da tela de testes.
  const serieFisica = useMemo(
    () => compilarSerieDeTestes({
      modalidade: atleta.modalidade,
      idadeEm: data => idadeDecimalEm(atleta.dataNascimento, data),
      coletas: testes,
    }),
    [atleta.modalidade, atleta.dataNascimento, testes],
  );

  const escores = useMemo(
    () => serieDeEscores({ tecnica: serie, fisica: serieFisica }),
    [serie, serieFisica],
  );

  const escalaDaModalidade = escalaDe(atleta.modalidade);
  const ponto = serie.find(p => p.data === abertaEm) || serie[serie.length - 1] || null;
  const escoreDoPonto = ponto
    ? escores.find(e => e.dataTecnica === ponto.data) || null
    : null;
  const laudo = ponto ? laudoTecnico(ponto) : null;

  async function imprimir() {
    setImprimindo(true);
    try {
      await gerarPDFAvaliacaoTecnica({
        atleta, serie, escores, nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar o PDF: ${e.message}`);
    } finally {
      setImprimindo(false);
    }
  }

  async function baixarFicha() {
    setGerandoFicha(true);
    try {
      await gerarPDFFichaDeAvaliacaoTecnica({
        atletas: [atleta], nucleo, nucleoNome: nomeDoNucleo(nucleo),
      });
    } catch (e) {
      onErro(`Não consegui gerar a ficha: ${e.message}`);
    } finally {
      setGerandoFicha(false);
    }
  }

  const semModalidade = !atleta.modalidade;
  const semCriterios = !semModalidade && modeloDe(atleta.modalidade).criterios.length === 0;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <Card>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap",
        }}>
          <div>
            <Btn variant="discreto" tamanho="sm" onClick={onVoltar}>← Todos os atletas</Btn>
            <h2 style={{ margin: "6px 0 0", ...TIPO.titulo, color: TEXTO }}>{atleta.nome}</h2>
            <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
              {atleta.modalidade || "sem modalidade"}
              {atleta.dataNascimento && ` · ${idadeEm(atleta.dataNascimento)} anos`}
              {ponto && ponto.tempoDePraticaMeses !== null
                && ` · ${tempoDePraticaEmTexto(ponto.tempoDePraticaMeses)} de prática`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {serie.length > 0 && (
              <Btn variant="secondary" onClick={imprimir} disabled={imprimindo}>
                {imprimindo ? "Gerando…" : "Imprimir em PDF"}
              </Btn>
            )}
            {podeEditar && (
              <Btn variant="secondary" onClick={baixarFicha} disabled={gerandoFicha}>
                {gerandoFicha ? "Gerando…" : "Ficha de avaliação em papel"}
              </Btn>
            )}
            {podeEditar && !semModalidade && (
              <Btn onClick={() => setEditando({ data: HOJE() })}>Nova avaliação</Btn>
            )}
          </div>
        </div>

        {semModalidade && (
          <div style={{ marginTop: ESP.lg }}>
            <StatusBox tipo="aviso" msg={
              "Falta a modalidade no cadastro deste atleta. Os critérios da avaliação "
              + "técnica são os do esporte, e sem ela não há como montar a ficha."
            } />
          </div>
        )}
        {semCriterios && (
          <div style={{ marginTop: ESP.lg }}>
            <StatusBox tipo="aviso" msg={
              `O programa ainda não tem o modelo de critérios de ${atleta.modalidade}. `
              + "A avaliação pode ser lançada com os critérios digitados à mão, e eles ficam "
              + "gravados dentro dela."
            } />
          </div>
        )}
      </Card>

      {editando && podeEditar && (
        <FormularioDeAvaliacao
          valores={editando}
          atleta={atleta}
          testes={testes}
          config={config}
          nucleo={nucleo}
          isMobile={isMobile}
          onCancelar={() => setEditando(null)}
          onSalvou={async () => { setEditando(null); await onSalvou(); }}
          onErro={onErro}
        />
      )}

      {serie.length === 0 ? (
        <Card><Vazio titulo="Este atleta ainda não tem avaliação técnica." /></Card>
      ) : (
        <>
          <Card titulo="Notas por critério">
            <TabelaDeCriterios serie={serie} isMobile={isMobile} />
            <LegendaDaEscala escala={escalaDaModalidade} />
            <ComoLer />
            {podeEditar && (
              <div style={{ display: "flex", gap: 8, marginTop: ESP.lg, flexWrap: "wrap" }}>
                {[...serie].reverse().map(p => (
                  <Btn key={p.data} variant="discreto" tamanho="sm"
                    onClick={() => setEditando(avaliacoes.find(a => a.data === p.data))}>
                    Corrigir {emBR(p.data)}
                  </Btn>
                ))}
              </div>
            )}
          </Card>

          {ponto && (
            <Card titulo="Leitura da avaliação">
              <div style={{ display: "grid", gap: ESP.lg }}>
                {serie.length > 1 && (
                  <div style={{ maxWidth: 260 }}>
                    <Sel label="Avaliação" value={ponto.data} onChange={setAbertaEm}
                      options={[...serie].reverse().map(p => ({
                        value: p.data, label: emBR(p.data),
                      }))} />
                  </div>
                )}
                <Detalhe ponto={ponto} laudo={laudo} isMobile={isMobile} />
              </div>
            </Card>
          )}

          {/* O escore global fecha a leitura da avaliação, porque é o que
              a coordenação pede, e abre a pendência quando a outra
              metade não foi lançada. */}
          <Card titulo="Escore global">
            <EscoreGlobal escore={escoreDoPonto || escoreGlobal({
              tecnica: ponto ? { somatorio: ponto.somatorio, divisor: ponto.divisor } : null,
            })} />
            <LegendaDosNiveis />
          </Card>

          <Card titulo="Evolução">
            <GradeDeGraficos serie={serie} isMobile={isMobile}
              modalidade={atleta.modalidade} />
          </Card>

          <Card titulo="O que mudou">
            <OQueMudou serie={serie} />
          </Card>
        </>
      )}
    </div>
  );
}

// ── Notas por critério ───────────────────────────────────────
//
// Um critério por linha e uma avaliação por coluna, com a diferença
// entre colunas vizinhas, no mesmo formato do quadro de medidas da
// avaliação física. É a forma que responde a pergunta de quem abre a
// tela: o que melhorou e o que não.
function TabelaDeCriterios({ serie, isMobile }) {
  const datas = serie.map(p => p.data);
  // Os ids na ordem em que aparecem, somando os critérios de todas as
  // datas: critério que saiu de uma avaliação continua na tabela, com a
  // célula vazia, em vez de desaparecer da história do atleta.
  const ids = [];
  const rotulos = {};
  serie.forEach(p => p.criterios.forEach(c => {
    if (!ids.includes(c.id)) ids.push(c.id);
    rotulos[c.id] = c.rotulo;
  }));

  const notaEm = (p, id) => {
    const c = p.criterios.find(x => x.id === id);
    return c && Number.isFinite(c.nota) ? c : null;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <thead>
          <tr>
            <Th>Critério</Th>
            {datas.map((d, i) => (
              <Fragment key={d}>
                <Th>{emBR(d)}</Th>
                {i > 0 && <Th>dif.</Th>}
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {ids.map(id => (
            <tr key={id}>
              <Td forte>{rotulos[id]}</Td>
              {serie.map((p, i) => {
                const atual = notaEm(p, id);
                const anterior = i > 0 ? notaEm(serie[i - 1], id) : null;
                const dif = atual && anterior ? atual.nota - anterior.nota : null;
                return (
                  <Fragment key={p.data}>
                    <Td>
                      {atual
                        ? pt(atual.nota, atual.nota % 1 ? 1 : 0)
                        : marcaNaCelula(p, id)}
                    </Td>
                    {i > 0 && (
                      <Td>
                        <Dif valor={dif} />
                      </Td>
                    )}
                  </Fragment>
                );
              })}
            </tr>
          ))}
          <tr>
            <Td forte>Somatório</Td>
            {serie.map((p, i) => (
              <Fragment key={p.data}>
                <Td forte>{pt(p.somatorio, 1)}</Td>
                {i > 0 && <Td><Dif valor={p.delta ? p.delta.somatorio : null} /></Td>}
              </Fragment>
            ))}
          </tr>
          <tr>
            <Td forte>Média</Td>
            {serie.map((p, i) => {
              const faixa = faixaDaNota(p.media);
              return (
                <Fragment key={p.data}>
                  <Td forte>
                    {pt(p.media, 2)}
                    {faixa && !isMobile && (
                      <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
                        {` ${faixa.rotulo.toLowerCase()}`}
                      </span>
                    )}
                  </Td>
                  {i > 0 && <Td><Dif valor={p.delta ? p.delta.media : null} /></Td>}
                </Fragment>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// O que a célula mostra quando não há nota: a marca de ausência quando
// ela existe, e o travessão quando o critério simplesmente não estava
// naquela avaliação.
function marcaNaCelula(ponto, id) {
  const c = ponto.criterios.find(x => x.id === id);
  if (c && c.ausencia) return rotuloDaAusencia(c.ausencia).toLowerCase();
  return "—";
}

function Dif({ valor }) {
  if (!Number.isFinite(valor) || valor === 0) {
    return <span style={{ color: CINZA_ESC }}>{valor === 0 ? "=" : "—"}</span>;
  }
  return (
    <span style={{ color: valor > 0 ? OK : RUIM, fontWeight: 600 }}>
      {valor > 0 ? "+" : "−"}{pt(Math.abs(valor), Math.abs(valor) % 1 ? 2 : 0)}
    </span>
  );
}

// ── Como ler ─────────────────────────────────────────────────
//
// O que a tabela não explica sozinha: de quem é a nota, e como ela
// vira média.
function ComoLer() {
  const [aberto, setAberto] = useState(false);

  const verbetes = [
    {
      titulo: "A nota é atribuída pelo técnico da modalidade",
      texto:
        "Cada critério recebe de 1 a 10, atribuído pelo técnico da modalidade a partir da "
        + "observação do gesto. Não é medida instrumental. Dois avaliadores calibram a "
        + "escala de modo diferente, "
        + "então a série vale para acompanhar o mesmo atleta, com o mesmo avaliador, e não "
        + "para comparar atletas entre si.",
    },
    {
      titulo: "O somatório e a média",
      texto:
        "O somatório é a soma das notas dos critérios. A média divide pelo número de "
        + "critérios da avaliação, inclusive os que ficaram em branco, porque o gesto que "
        + "não foi avaliado continua fazendo parte do esporte. A média é o que entra no "
        + "escore global, com peso de 75%.",
    },
  ];

  return (
    <div style={{ marginTop: ESP.md }}>
      <button onClick={() => setAberto(a => !a)} style={{
        background: "none", border: "none", padding: 0, cursor: "pointer",
        font: "inherit", ...TIPO.apoio, color: AZUL, fontWeight: 600,
      }}>
        {aberto ? "Esconder como ler" : "Como ler estes números"}
      </button>
      {aberto && (
        <div style={{ display: "grid", gap: ESP.md, marginTop: ESP.md }}>
          {verbetes.map(v => (
            <div key={v.titulo}>
              <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{v.titulo}</div>
              <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>{v.texto}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detalhe de uma avaliação ─────────────────────────────────

function Detalhe({ ponto, laudo, isMobile }) {
  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <FileiraDeEstatisticas>
        <Estatistica rotulo="Média" valor={pt(ponto.media, 2)} />
        <Estatistica rotulo="Somatório" valor={`${pt(ponto.somatorio, 1)} de ${ponto.maximo}`} />
        <Estatistica rotulo="Faixa" valor={laudo ? laudo.classificacao : "—"} />
        <Estatistica rotulo="Avaliador" valor={ponto.avaliador || "não informado"} />
      </FileiraDeEstatisticas>

      {(ponto.periodoDe || ponto.periodoAte) && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
          {`Observação de ${emBR(ponto.periodoDe)} a ${emBR(ponto.periodoAte)}: a nota resume `}
          {"o período de observação, e não uma sessão de treino."}
        </div>
      )}

      {!ponto.completa && ponto.faltam.length > 0 && (
        <StatusBox tipo="aviso" msg={
          `Esta avaliação ficou sem nota em ${ponto.faltam.length} `
          + `critério${ponto.faltam.length > 1 ? "s" : ""}: ${ponto.faltam.join(", ")}. `
          + "A média divide pelo total de critérios, então ela resulta inferior ao "
          + "desempenho efetivamente avaliado."
        } />
      )}

      {/* Ausência não é falta de avaliação, e a tela precisa dizer a
          diferença: o critério saiu do divisor, e a média foi tirada de
          menos critérios do que a lista tem. */}
      {ponto.ausentes && ponto.ausentes.length > 0 && (
        <StatusBox tipo="aviso" msg={
          `${ponto.ausentes.map(a => `${a.rotulo} (${a.motivo.toLowerCase()})`).join(", ")}. `
          + `Critério com ausência registrada sai do divisor, então a média deste dia foi `
          + `tirada de ${ponto.divisor} critério${ponto.divisor > 1 ? "s" : ""} e não de `
          + `${ponto.criterios.length}. Registrar a ausência como nota baixa penalizaria `
          + "o atleta por uma avaliação que ele não pôde fazer."
        } />
      )}

      {laudo && (
        <div>
          <div style={{ ...TIPO.corpo, color: TEXTO, lineHeight: 1.65 }}>{laudo.texto}</div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6, marginTop: ESP.sm }}>
            {laudo.ressalva}
          </div>
        </div>
      )}

      {/* O que o avaliador olha dentro de cada critério. No BMX isso vem
          escrito na própria planilha, em três itens por trecho da pista,
          e é a diferença entre "deu 7 na largada" e saber em quê. */}
      {porGrupo(ponto.criterios).map(({ grupo, itens }) => (
      <div key={grupo || "sem-grupo"}>
      {grupo && (
        <div style={{ ...TIPO.apoio, color: AZUL, fontWeight: 700, marginBottom: 6 }}>
          {grupo}
        </div>
      )}
      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
      }}>
        {itens.map(c => {
          const faixa = c.faixa;
          return (
            <div key={c.id} style={{
              border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 12px",
              background: FUNDO_SUAVE,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{c.rotulo}</span>
                <span style={{ ...TIPO.corpo, fontWeight: 700, color: TEXTO }}>
                  {/* Sempre com uma casa, como no relatório: a mesma
                      nota escrita "7" na tela e "7,0" no papel faz quem
                      confere um contra o outro parar para checar. */}
                  {pt(c.nota, 1)}
                </span>
              </div>
              <div style={{ ...TIPO.apoio, color: c.ausencia ? ATENCAO : CINZA_ESC }}>
                {faixa ? faixa.rotulo : c.ausencia ? rotuloDaAusencia(c.ausencia) : "sem nota"}
              </div>
              {c.detalhe && (
                <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 4 }}>{c.detalhe}</div>
              )}
              {c.observa && c.observa.length > 0 && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                  {c.observa.map(o => (
                    <li key={o} style={{ ...TIPO.apoio, color: CINZA_ESC }}>{o}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      </div>
      ))}

      {ponto.observa && ponto.observa.length > 0 && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
          Em cada critério, o avaliador observa: {ponto.observa.join(", ").toLowerCase()}.
        </div>
      )}

      {ponto.observacoes && (
        <div>
          <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
            Observações do avaliador
          </div>
          <div style={{ ...TIPO.corpo, color: TEXTO, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            {ponto.observacoes}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Evolução ─────────────────────────────────────────────────
//
// Um gráfico por critério, pequeno, com a mesma escala de 1 a 10 em
// todos. A escala fixa aqui é diferente da avaliação física, onde cada
// medida tem a sua: a nota já nasce numa escala comum, e deixar o eixo
// se ajustar a cada critério faria meio ponto parecer salto num gráfico
// e nada no de ao lado.
//
// Nos critérios de tempo o gráfico é do TEMPO, em segundos, e não da
// nota. A nota depende do grupo do dia e desenharia queda onde o atleta
// melhorou.
function GradeDeGraficos({ serie, isMobile, modalidade }) {
  const ids = [];
  const rotulos = {};
  serie.forEach(p => p.criterios.forEach(c => {
    if (!ids.includes(c.id)) ids.push(c.id);
    rotulos[c.id] = c.rotulo;
  }));

  const indicadores = [
    {
      id: "media",
      rotulo: "Média dos critérios",
      unidade: "",
      casas: 2,
      amplitudeMinima: 2,
      passo: 0.5,
      valor: p => p.media,
    },
    ...ids.map(id => ({
      id,
      rotulo: rotulos[id],
      unidade: "",
      casas: 1,
      amplitudeMinima: 4,
      passo: 1,
      valor: p => {
        const c = p.criterios.find(x => x.id === id);
        return c ? c.nota : null;
      },
    })),
  ];

  return (
    <>
      <div style={{
        display: "grid", gap: ESP.xl,
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
      }}>
        {indicadores.map(ind => (
          <GraficoDeMedida key={ind.id} serie={serie} indicador={ind} />
        ))}
      </div>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: ESP.md, lineHeight: 1.6 }}>
        A escala vertical é a mesma em todos os critérios, de 1 a 10, para que a mesma
        variação tenha a mesma amplitude em todos os gráficos.
        {/* O tempo de pista existe no BMX e em modalidade nenhuma mais.
            Dizer onde ele está na tela do tênis de mesa mandava o técnico
            procurar um gráfico que a modalidade dele não tem. */}
        {modalidade === "BMX" && (
          " O tempo de pista não está aqui: é medida cronometrada e tem gráfico próprio, "
          + "em segundos, na aba de testes físicos."
        )}
      </div>
    </>
  );
}

// ── O que mudou ──────────────────────────────────────────────

function OQueMudou({ serie }) {
  const ultimo = serie[serie.length - 1];
  if (!ultimo || !ultimo.delta) {
    return <Vazio titulo="A comparação aparece a partir da segunda avaliação." />;
  }
  const blocos = [
    { titulo: `Desde a avaliação anterior`, v: ultimo.delta, de: serie[serie.length - 2].data },
    ...(serie.length > 2
      ? [{ titulo: "Desde o início", v: ultimo.desdeAPrimeira, de: ultimo.dataDaPrimeira }]
      : []),
  ];

  return (
    <div style={{ display: "grid", gap: ESP.xl }}>
      {blocos.map(b => (
        <div key={b.titulo}>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700 }}>
            {b.titulo}
            <span style={{ ...TIPO.apoio, fontWeight: 400, color: CINZA_ESC }}>
              {` · de ${emBR(b.de)} para ${emBR(ultimo.data)}`}
            </span>
          </div>
          <div style={{
            display: "grid", gap: 8, marginTop: ESP.md,
            gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
          }}>
            <Variacao rotulo="Média" valor={b.v.media} casas={2} />
            <Variacao rotulo="Somatório" valor={b.v.somatorio} casas={1} />
            {b.v.porCriterio.map(c => (
              <Variacao
                key={c.id}
                rotulo={c.rotulo}
                valor={c.delta}
                casas={1}
              />
            ))}
          </div>
          {(b.v.criteriosNovos.length > 0 || b.v.criteriosSaidos.length > 0) && (
            <div style={{ ...TIPO.apoio, color: ATENCAO, marginTop: ESP.md, lineHeight: 1.6 }}>
              {b.v.criteriosNovos.length > 0
                && `Critérios que entraram: ${b.v.criteriosNovos.join(", ")}. `}
              {b.v.criteriosSaidos.length > 0
                && `Critérios que saíram: ${b.v.criteriosSaidos.join(", ")}. `}
              Critério que não existe nas duas datas não entra na comparação.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Variacao({ rotulo, valor, casas = 1, nota }) {
  const cor = !Number.isFinite(valor) || valor === 0 ? CINZA_ESC : valor > 0 ? OK : RUIM;
  return (
    <div style={{ border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "9px 11px" }}>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>{rotulo}</div>
      <div style={{ ...TIPO.corpo, fontWeight: 700, color: cor }}>
        {!Number.isFinite(valor)
          ? "—"
          : `${valor > 0 ? "+" : valor < 0 ? "−" : ""}${pt(Math.abs(valor), casas)}`}
      </div>
      {nota && <div style={{ ...TIPO.apoio, color: ATENCAO, lineHeight: 1.5 }}>{nota}</div>}
    </div>
  );
}

// ── Formulário ───────────────────────────────────────────────

function FormularioDeAvaliacao({
  valores, atleta, testes, config, nucleo, isMobile, onCancelar, onSalvou, onErro,
}) {
  const modalidade = atleta.modalidade || "";
  const modelo = modeloDe(modalidade);

  const [form, setForm] = useState(() => {
    // A lista gravada é a que vale, e por isso corrigir uma avaliação de
    // janeiro não a reescreve com a ficha de hoje. Só a coleta NOVA nasce
    // da ficha da modalidade, que é o modelo mais o que a comissão
    // acrescentou.
    const gravados = Array.isArray(valores.criterios) && valores.criterios.length
      ? valores.criterios
      : criteriosDaModalidade(modalidade, config);
    return {
      data: valores.data || HOJE(),
      periodoDe: valores.periodoDe || "",
      periodoAte: valores.periodoAte || "",
      avaliador: valores.avaliador || "",
      tempoDePraticaMeses: valores.tempoDePraticaMeses ?? "",
      observacoes: valores.observacoes || "",
      criterios: gravados.map(c => ({
        id: c.id,
        rotulo: c.rotulo,
        grupo: c.grupo || null,
        nota: c.nota ?? "",
        // A marca de ausência vem junto. Sem ela aqui, abrir e gravar de
        // novo uma avaliação apagava o "dispensa médica" do critério e
        // mudava a média, porque o critério voltava ao divisor.
        ausencia: c.ausencia || "",
      })),
    };
  });
  const [salvando, setSalvando] = useState(false);
  const [insistindo, setInsistindo] = useState(false);

  const escalaDaModalidade = escalaDe(modalidade);
  const mudar = (campo, v) => setForm(f => ({ ...f, [campo]: v }));
  const mudarNota = (id, v) => setForm(f => ({
    ...f,
    criterios: f.criterios.map(c => (c.id === id ? { ...c, nota: v } : c)),
  }));
  // A marca de ausência e a nota se excluem: o critério tem uma nota ou
  // tem um motivo para não ter nenhuma.
  const mudarAusencia = (id, v) => setForm(f => ({
    ...f,
    criterios: f.criterios.map(c => (
      c.id === id ? { ...c, ausencia: v, nota: v ? "" : c.nota } : c
    )),
  }));

  // Um critério a mais, digitado na hora.
  //
  // Existe por causa de núcleo novo e de modalidade que o programa ainda
  // não atende: a lista do código é sugestão, e o que vale é a lista
  // gravada na avaliação. Sem isto, atender outro esporte exigiria mexer
  // no programa.
  function acrescentarCriterio() {
    const n = form.criterios.length + 1;
    setForm(f => ({
      ...f,
      criterios: [...f.criterios, { id: `extra${n}`, rotulo: "", nota: "", novo: true }],
    }));
  }
  function mudarRotulo(id, v) {
    setForm(f => ({
      ...f,
      criterios: f.criterios.map(c => (c.id === id ? { ...c, rotulo: v } : c)),
    }));
  }
  function removerCriterio(id) {
    setForm(f => ({ ...f, criterios: f.criterios.filter(c => c.id !== id) }));
  }

  // O teste físico do mesmo dia, se existir. Serve para o avaliador ver
  // que a outra metade da avaliação já foi lançada, sem sair da tela.
  const testeDoDia = testes.find(t => t.data === form.data) || null;

  // Critério com ausência marcada não está pendente: ele tem resposta,
  // e a resposta é que o atleta não pôde ser avaliado nele.
  const pendencias = form.criterios
    .filter(c => !String(c.nota ?? "").trim() && !c.ausencia)
    .map(c => c.rotulo || "critério sem nome");
  const semRotulo = form.criterios.filter(c => !String(c.rotulo || "").trim()).length;
  const foraDaEscala = form.criterios.filter(c => {
    const n = Number(String(c.nota ?? "").replace(",", "."));
    return String(c.nota ?? "").trim() && (!Number.isFinite(n) || n < NOTA_MINIMA || n > NOTA_MAXIMA);
  });

  async function salvar() {
    if (!form.data) return onErro("A avaliação precisa de uma data.");
    if (semRotulo) {
      return onErro(
        "Há critério sem nome. O nome é gravado na avaliação e é por ele que a nota "
        + "será lida depois.",
      );
    }
    if (foraDaEscala.length) {
      return onErro(
        `A escala vai de ${NOTA_MINIMA} a ${NOTA_MAXIMA}, e `
        + `${foraDaEscala.map(c => c.rotulo).join(", ")} está fora dela.`,
      );
    }
    // Avaliação incompleta não grava na primeira tentativa, pelo mesmo
    // motivo da avaliação física: foi assim que as coletas de 2026
    // ficaram sem medida e ninguém soube por meses. A segunda tentativa
    // grava, porque gesto que o atleta não fez no dia é caso real.
    if (pendencias.length && !insistindo) {
      setInsistindo(true);
      return onErro(
        `Faltam ${pendencias.length} nota(s) para a avaliação ficar completa: `
        + `${pendencias.join(", ")}. Preencha o que faltar e grave novamente, ou confirme `
        + "a gravação para registrar a avaliação incompleta.",
      );
    }

    setSalvando(true);
    try {
      await salvarAvaliacaoTecnica({
        nucleo,
        atletaUid: atleta.uid,
        atletaNome: atleta.nome,
        modalidade,
        data: form.data,
        periodoDe: form.periodoDe || null,
        periodoAte: form.periodoAte || null,
        avaliador: form.avaliador.trim() || null,
        tempoDePraticaMeses: numeroOuNulo(form.tempoDePraticaMeses),
        observacoes: form.observacoes.trim() || "",
        // A lista e o divisor vão gravados. É o que reproduz esta nota
        // daqui a dois anos, mesmo que os critérios da modalidade mudem.
        criterios: form.criterios.map(c => limparCriterio(c)),
        // O divisor desconta os critérios com ausência marcada, que é a
        // regra que a tela e o relatório explicam: "em branco" é falha de
        // avaliação e pesa; "não pôde ser avaliado" é fato do dia e sai
        // da conta. Gravar o total aqui faria a média do documento
        // discordar da regra que a própria tela descreve.
        divisor: Math.max(
          1,
          form.criterios.length - form.criterios.filter(c => c.ausencia).length,
        ),
      });
      await onSalvou();
    } catch (e) {
      onErro(`Não consegui gravar: ${e.message}`);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card titulo="Avaliação técnica">
      <div style={{ display: "grid", gap: ESP.lg }}>
        <div style={{
          display: "grid", gap: 10,
          gridTemplateColumns: isMobile ? "1fr" : "190px minmax(200px, 1fr) 190px",
        }}>
          <Input label="Data da avaliação" type="date" value={form.data}
            onChange={v => mudar("data", v)} />
          <Input label={`Avaliador (${(modelo.avaliador || "Técnico").toLowerCase()})`}
            value={form.avaliador} onChange={v => mudar("avaliador", v)}
            placeholder="quem deu as notas" />
          <Input label="Tempo de prática (meses)" type="number"
            value={form.tempoDePraticaMeses}
            onChange={v => mudar("tempoDePraticaMeses", v)} />
        </div>

        {/* O período de observação, que a ficha do tênis de mesa chama de
            "tempo de treino". A nota do técnico sai de uma semana de
            treino, e não de uma sessão: sem os dois campos, a folha
            impressa diz uma data que ninguém usou para avaliar. */}
        <div style={{
          display: "grid", gap: 10,
          gridTemplateColumns: isMobile ? "1fr" : "190px 190px minmax(200px, 1fr)",
        }}>
          <Input label="Observação de" type="date" value={form.periodoDe}
            onChange={v => mudar("periodoDe", v)} />
          <Input label="Observação até" type="date" value={form.periodoAte}
            onChange={v => mudar("periodoAte", v)} />
          <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 20, lineHeight: 1.5 }}>
            O intervalo de treino que a nota resume. Em branco, vale o dia da avaliação.
          </div>
        </div>

        {form.tempoDePraticaMeses !== "" && tempoDePraticaEmTexto(form.tempoDePraticaMeses) && (
          <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
            {tempoDePraticaEmTexto(form.tempoDePraticaMeses)} de prática no esporte.
          </div>
        )}

        {modelo.observa && modelo.observa.length > 0 && (
          <div style={{
            background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
            padding: "10px 12px", ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6,
          }}>
            Em cada critério, observe: {modelo.observa.join(", ").toLowerCase()}.
          </div>
        )}

        <LegendaDaEscala escala={escalaDaModalidade} />

        <div>
          <h4 style={{ margin: "0 0 8px", ...TIPO.corpo, color: TEXTO }}>
            Notas por critério
            <span style={{ fontWeight: 400, color: CINZA_ESC }}>
              {` (${form.criterios.length} ao todo, de ${NOTA_MINIMA} a ${NOTA_MAXIMA}, aceita decimal)`}
            </span>
          </h4>
          {/* Agrupado, e não em lista corrida. A ficha do tênis de mesa
              tem dezenove critérios: sem os grupos, quem avalia percorre
              dezenove caixas iguais e perde o lugar no meio da série de
              golpes. Com eles, a tela tem a forma da folha de papel que
              o técnico já usa. */}
          {porGrupo(form.criterios).map(({ grupo, itens }) => (
          <div key={grupo || "sem-grupo"} style={{ marginBottom: ESP.md }}>
          {grupo && (
            <div style={{ ...TIPO.apoio, color: AZUL, fontWeight: 700, margin: "4px 0 6px" }}>
              {grupo}
            </div>
          )}
          <div style={{
            display: "grid", gap: 10,
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(230px, 1fr))",
          }}>
            {itens.map(c => {
              const base = modelo.criterios.find(x => x.id === c.id) || {};
              const faixa = faixaDaNota(Number(String(c.nota ?? "").replace(",", ".")));
              const pendente = !String(c.nota ?? "").trim();
              return (
                <div key={c.id} style={{
                  border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "10px 11px",
                }}>
                  {c.novo ? (
                    <Input label="Nome do critério" value={c.rotulo}
                      onChange={v => mudarRotulo(c.id, v)} placeholder="o gesto avaliado" />
                  ) : (
                    <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
                      {c.rotulo}
                      {base.detalhe && (
                        <span style={{ fontWeight: 400, color: CINZA_ESC }}>
                          {` · ${base.detalhe}`}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 6 }}>
                    <Input label="Nota" type="number" value={c.nota}
                      onChange={v => mudarNota(c.id, v)} disabled={!!c.ausencia} />
                    <div style={{
                      ...TIPO.apoio, marginTop: 2,
                      color: pendente && !c.ausencia ? ATENCAO : CINZA_ESC,
                    }}>
                      {c.ausencia
                        ? rotuloDaAusencia(c.ausencia)
                        : pendente ? "sem nota" : faixa ? faixa.rotulo : ""}
                    </div>
                  </div>

                  {/* O critério que não pôde ser avaliado tem resposta
                      própria, e ela não é nota baixa nem campo vazio: sai
                      do divisor, e a média do dia é tirada dos outros. */}
                  <div style={{ marginTop: 6 }}>
                    <Sel label="Sem avaliação" value={c.ausencia || ""}
                      onChange={v => mudarAusencia(c.id, v)} options={MARCAS_DE_AUSENCIA} />
                  </div>

                  {base.observa && base.observa.length > 0 && (
                    <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                      {base.observa.map(o => (
                        <li key={o} style={{ ...TIPO.apoio, color: CINZA_ESC }}>{o}</li>
                      ))}
                    </ul>
                  )}
                  {c.novo && (
                    <Btn variant="discreto" tamanho="sm" style={{ marginTop: 8 }}
                      onClick={() => removerCriterio(c.id)}>
                      Remover
                    </Btn>
                  )}
                </div>
              );
            })}
          </div>
          </div>
          ))}
          <Btn variant="discreto" tamanho="sm" style={{ marginTop: 10 }}
            onClick={acrescentarCriterio}>
            Acrescentar critério
          </Btn>
        </div>

        <Input label="Observações" value={form.observacoes}
          onChange={v => mudar("observacoes", v)}
          placeholder="o que não cabe em nota" />

        {/* A outra metade da avaliação, dita aqui para o técnico não
            achar que terminou. */}
        <StatusBox tipo={testeDoDia ? "ok" : "aviso"} msg={
          testeDoDia
            ? "Os testes físicos desta data já foram lançados, então o escore global deste "
              + "atleta será calculado."
            : "Ainda não há teste físico lançado nesta data. O escore global é 75% desta "
              + "avaliação e 25% das capacidades físicas, e não é calculado sem as duas partes."
        } />

        {pendencias.length > 0 && (
          <StatusBox tipo={insistindo ? "erro" : "aviso"} msg={
            `Faltam ${pendencias.length} nota(s): ${pendencias.join(", ")}.`
            + (insistindo
              ? " Gravar novamente registra a avaliação incompleta, e a média é dividida"
                + " pelo total de critérios."
              : "")
          } />
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={salvar} disabled={salvando}>
            {salvando
              ? "Gravando..."
              : insistindo && pendencias.length
                ? `Gravar incompleta (faltam ${pendencias.length})`
                : "Gravar avaliação"}
          </Btn>
          <Btn variant="secondary" onClick={onCancelar} disabled={salvando}>Cancelar</Btn>
        </div>
      </div>
    </Card>
  );
}

// ── Apoio ────────────────────────────────────────────────────

function Th({ children }) {
  return (
    <th style={{
      textAlign: "left", padding: "7px 9px", borderBottom: `1px solid ${BORDA}`,
      color: CINZA_ESC, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {children}
    </th>
  );
}

function Td({ children, forte }) {
  return (
    <td style={{
      padding: "7px 9px", borderBottom: `1px solid ${BORDA}`,
      color: TEXTO, fontWeight: forte ? 700 : 400, whiteSpace: "nowrap",
    }}>
      {children}
    </td>
  );
}

function pt(v, casas = 1) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(casas).replace(".", ",") : "—";
}

function emBR(iso) {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return d ? `${d}/${m}/${a}` : iso;
}

function numeroOuNulo(v) {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

// O critério como ele vai para o banco: sem o rascunho da tela e sem
// campo vazio, que no Firestore ocuparia espaço dizendo nada.
function limparCriterio(c) {
  const saida = { id: c.id, rotulo: String(c.rotulo || "").trim() };
  const nota = numeroOuNulo(c.nota);
  if (nota !== null) saida.nota = nota;
  // A marca só vale sem nota. Critério com nota e marca ao mesmo tempo é
  // estado impossível, e aqui a nota ganha.
  if (nota === null && c.ausencia) saida.ausencia = c.ausencia;
  // O grupo acompanha o critério no banco. Com dezenove critérios, é ele
  // que separa golpe de leitura de jogo na leitura de amanhã.
  if (c.grupo) saida.grupo = c.grupo;
  return saida;
}
